/**
 * Manage → Made by the platform.
 *
 * The write policy's payoff as a screen (`docs/data-loop-plan.md` §4). Because
 * everything the agent and its apps write is a NEW object — never an edit of
 * something a person made — "undo" is just "delete what the platform made",
 * and this is the one page that lists exactly that: files (artifacts and
 * attachments with `created_by_kind` agent/app) and published tables (read off
 * the data-plane audit trail).
 *
 * Two sections have no delete on purpose (F6): files the platform added to a
 * connected DRIVE, and mail it drafted or sent. Those objects are not
 * Chaidojo's to remove — a file lives in somebody's OneDrive and a draft in
 * their mailbox, and they delete it there, in the tool they already use. A
 * button here would mean asking for a delete scope the platform deliberately
 * never requests. So they are a record: what left, where it went, when.
 *
 * Two things it deliberately does NOT do. It never shows a row a person owns —
 * the API filters on the owner kind and re-checks it on every delete, so an
 * upload is invisible here even to a URL typed by hand. And it never deletes
 * on its own: every row here is removed by an org-admin clicking, which is the
 * half of the policy that says the platform creates and the person disposes.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@chai/api-client";

import { useApi } from "@/api";
import { PageHeader } from "@/components/manage/PageHeader";
import { EmptyRow, Section } from "@/components/manage/Section";
import { StatStrip } from "@/components/manage/StatStrip";
import { useIsDemoManage } from "@/components/manage/org-context";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/manage/platform-writes")({
  component: PlatformWritesPage,
});

interface PlatformObject {
  kind: "artifact" | "attachment";
  id: string;
  name: string;
  created_by_kind: string;
  creator_ref: string | null;
  workspace_id: string;
  canvas_id: string | null;
  canvas_title: string | null;
  format: string | null;
  size_bytes: number | null;
  created_at: string | null;
}

interface PublishedTable {
  id: string;
  principal_kind: string;
  principal_id: string;
  workspace_id: string;
  table: string;
  duckdb_table: string | null;
  rows: number;
  bytes: number;
  published_at: string | null;
}

interface DriveFile {
  id: string;
  principal_kind: string;
  principal_id: string;
  workspace_id: string;
  connection: string;
  path: string;
  bytes: number;
  created_at: string | null;
}

interface MailWrite {
  id: string;
  verb: "mail_draft" | "mail_send";
  principal_kind: string;
  principal_id: string;
  workspace_id: string;
  connection: string;
  subject: string;
  to: string;
  created_at: string | null;
}

interface PlatformWrites {
  objects: PlatformObject[];
  tables: PublishedTable[];
  drive_files: DriveFile[];
  mail: MailWrite[];
  drafts_total: number;
  sends_total: number;
}

const KEY = ["admin-platform-writes"] as const;

const TH =
  "px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground";

function when(value: string | null): string {
  if (!value) return "—";
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? "—" : at.toLocaleString();
}

function size(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "agent · 01M28…" — the kind is what the policy turns on, the ref is who. */
function creator(kind: string, ref: string | null): string {
  return ref ? `${kind} · ${ref}` : kind;
}

function PlatformWritesPage() {
  const api = useApi();
  const qc = useQueryClient();
  const isDemo = useIsDemoManage();

  const writes = useQuery({
    queryKey: KEY,
    enabled: !isDemo,
    queryFn: () => api.request<PlatformWrites>("/admin/platform-writes"),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  const deleteObject = useMutation({
    mutationFn: (row: PlatformObject) =>
      api.request<void>(
        `/admin/platform-writes/${row.kind === "artifact" ? "artifacts" : "attachments"}/${row.id}`,
        { method: "DELETE" },
        false,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast.success("Removed");
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Could not remove it");
    },
  });

  const deleteTable = useMutation({
    mutationFn: (row: PublishedTable) =>
      api.request<void>(
        "/admin/platform-writes/tables/delete",
        {
          method: "POST",
          body: JSON.stringify({
            workspace_id: row.workspace_id,
            principal_kind: row.principal_kind,
            principal_id: row.principal_id,
            table: row.table,
          }),
        },
        false,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      toast.success("Table removed");
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Could not remove the table");
    },
  });

  if (isDemo) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10 text-sm text-muted-foreground">
        Not available in this demo.
      </div>
    );
  }

  const notEnabled =
    writes.isError && writes.error instanceof ApiError && writes.error.status === 404;
  const objects = writes.data?.objects ?? [];
  const tables = writes.data?.tables ?? [];
  const driveFiles = writes.data?.drive_files ?? [];
  const mail = writes.data?.mail ?? [];
  const busy = deleteObject.isPending || deleteTable.isPending;

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Write-back"
        title="Made by the platform"
        description="Every file and table the agent or an app created. Nothing a person made is listed here, and nothing here is removed except by you."
      />

      {notEnabled ? (
        <p className="text-sm text-muted-foreground">
          The workspace data plane is not enabled on this deployment.
        </p>
      ) : (
        <>
          <StatStrip
            items={[
              { label: "Files", value: objects.length, hint: "artifacts + attachments" },
              { label: "Tables", value: tables.length, hint: "published into mojo" },
              {
                label: "Published",
                value: size(tables.reduce((sum, t) => sum + t.bytes, 0)),
                hint: "most recent write of each",
              },
              {
                label: "Rows",
                value: tables.reduce((sum, t) => sum + t.rows, 0),
                hint: "across those tables",
              },
              {
                label: "On drives",
                value: driveFiles.length,
                hint: "files added, never replaced",
              },
              {
                label: "Mail",
                value: `${writes.data?.drafts_total ?? 0} / ${writes.data?.sends_total ?? 0}`,
                hint: "drafted / sent, all time",
              },
            ]}
          />

          <Section
            title="Files"
            description="Artifacts and attachments created by the agent or by an app. Deleting one is permanent."
          >
            {writes.isLoading ? (
              <Skeleton className="h-40 w-full rounded-md" />
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <th className={TH}>Name</th>
                      <th className={TH}>Made by</th>
                      <th className={TH}>Where</th>
                      <th className={TH}>Kind</th>
                      <th className={TH}>When</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {objects.length ? (
                      objects.map((row) => (
                        <tr
                          key={`${row.kind}:${row.id}`}
                          className="group transition hover:bg-[var(--color-surface)]"
                        >
                          <td className="px-4 py-3 font-medium text-foreground">{row.name}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {creator(row.created_by_kind, row.creator_ref)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {row.canvas_title ?? row.workspace_id}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {row.kind === "artifact"
                              ? (row.format ?? "artifact")
                              : `${row.format ?? "file"} · ${size(row.size_bytes)}`}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {when(row.created_at)}
                          </td>
                          <td className="px-2 py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={busy}
                              className="size-7 opacity-0 transition group-hover:opacity-100"
                              aria-label={`Delete ${row.name}`}
                              onClick={() => {
                                if (confirm(`Delete "${row.name}"? This cannot be undone.`)) {
                                  deleteObject.mutate(row);
                                }
                              }}
                            >
                              <Trash2 className="size-3.5 text-muted-foreground" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <EmptyRow colSpan={6}>
                        Nothing yet — the agent and its apps have not created any files.
                      </EmptyRow>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section
            title="Published tables"
            description="Tables written back into the data platform. Deleting one removes the table; data people uploaded is untouched."
          >
            {writes.isLoading ? (
              <Skeleton className="h-32 w-full rounded-md" />
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <th className={TH}>Table</th>
                      <th className={TH}>Published by</th>
                      <th className={TH}>Queryable as</th>
                      <th className={TH}>Size</th>
                      <th className={TH}>When</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {tables.length ? (
                      tables.map((row) => (
                        <tr
                          key={row.id}
                          className="group transition hover:bg-[var(--color-surface)]"
                        >
                          <td className="px-4 py-3 font-medium text-foreground">{row.table}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {creator(row.principal_kind, row.principal_id)}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                            {row.duckdb_table ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {row.rows} rows · {size(row.bytes)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {when(row.published_at)}
                          </td>
                          <td className="px-2 py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={busy}
                              className="size-7 opacity-0 transition group-hover:opacity-100"
                              aria-label={`Delete table ${row.table}`}
                              onClick={() => {
                                if (
                                  confirm(
                                    `Delete the table "${row.table}"? Anything reading it will stop working.`,
                                  )
                                ) {
                                  deleteTable.mutate(row);
                                }
                              }}
                            >
                              <Trash2 className="size-3.5 text-muted-foreground" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <EmptyRow colSpan={6}>Nothing published yet.</EmptyRow>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section
            title="Files created on drives"
            description="Files the agent or an app ADDED to a connected drive, under its /Chaidojo folder. Nothing already in the drive was touched. To remove one, delete it in the drive itself — Chaidojo never asks for permission to delete your files."
          >
            {writes.isLoading ? (
              <Skeleton className="h-24 w-full rounded-md" />
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <th className={TH}>Path</th>
                      <th className={TH}>Drive</th>
                      <th className={TH}>Made by</th>
                      <th className={TH}>Size</th>
                      <th className={TH}>When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {driveFiles.length ? (
                      driveFiles.map((row) => (
                        <tr key={row.id} className="transition hover:bg-[var(--color-surface)]">
                          <td className="px-4 py-3 font-mono text-[11px] text-foreground">
                            {row.path}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {row.connection}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {creator(row.principal_kind, row.principal_id)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {size(row.bytes)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {when(row.created_at)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <EmptyRow colSpan={5}>
                        Nothing yet — no file has been written to a connected drive.
                      </EmptyRow>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section
            title="Mail drafted / sent"
            description="Drafts left for someone to review, and messages an approved app sent. The agent can only draft. Delete or unsend in the mailbox — there is no verb here that reaches a message."
          >
            {writes.isLoading ? (
              <Skeleton className="h-24 w-full rounded-md" />
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <th className={TH}>Subject</th>
                      <th className={TH}>To</th>
                      <th className={TH}>Mailbox</th>
                      <th className={TH}>What</th>
                      <th className={TH}>Made by</th>
                      <th className={TH}>When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {mail.length ? (
                      mail.map((row) => (
                        <tr key={row.id} className="transition hover:bg-[var(--color-surface)]">
                          <td className="px-4 py-3 font-medium text-foreground">{row.subject}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{row.to}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {row.connection}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {row.verb === "mail_send" ? "sent" : "draft"}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {creator(row.principal_kind, row.principal_id)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {when(row.created_at)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <EmptyRow colSpan={6}>
                        Nothing yet — no draft has been left and no message sent.
                      </EmptyRow>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
